import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldAlert, CheckCircle2, Loader2, Lock as LockIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { IsometricStack, FloorPlanFragment } from "@/components/landing/Blueprint";

const ease = [0.22, 1, 0.36, 1] as [number, number, number, number];

export default function CreateAccount() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = params.get("invite") || "";

  const [status, setStatus] = useState<"loading" | "valid" | "invalid" | "used">("loading");
  const [email, setEmail] = useState("");
  // Both come from the invite, which carries what the user gave on the waitlist.
  const [fullName, setFullName] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    supabase
      .rpc("validate_invite_token" as any, { _invite_token: token })
      .then(({ data }: any) => {
        const row = Array.isArray(data) ? data[0] : data;
        if (!row || row.status !== "approved") {
          setStatus("invalid");
        } else if (row.invite_used) {
          setStatus("used");
        } else {
          setEmail(row.email);
          setFullName([row.first_name, row.last_name].filter(Boolean).join(" ").trim());
          setWorkspaceName((row.company ?? "").trim());
          setStatus("valid");
        }
      });
  }, [token]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirmPassword) { setError("Passwords do not match."); return; }

    setCreating(true);
    // These reach handle_new_user as raw_user_meta_data. Signing up with an
    // empty full_name is why invited users have been landing in a workspace
    // called "My's Workspace" — the name they gave on the waitlist was sitting
    // in the invite the whole time.
    const { error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName, workspace_name: workspaceName } },
    });

    if (signUpError) {
      setError(signUpError.message);
      setCreating(false);
      return;
    }

    await supabase.rpc("mark_invite_used" as any, { _invite_token: token });
    toast({ title: "Account created", description: "Check your email to verify, then sign in." });
    navigate("/auth");
  };

  // Shared shell wrapper
  const Shell = ({ children }: { children: React.ReactNode }) => (
    <div className="min-h-screen relative overflow-hidden bg-[hsl(var(--bone))] text-[hsl(var(--ink))]">
      <div className="brand-aurora" aria-hidden="true" />
      <div className="absolute inset-0 brand-grid-bg opacity-60 pointer-events-none [mask-image:radial-gradient(ellipse_60%_60%_at_50%_40%,#000_30%,transparent_80%)]" aria-hidden="true" />
      <header className="relative z-10 px-6 lg:px-10 py-6 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-[hsl(var(--brand-forest))] flex items-center justify-center">
            <span className="font-serif italic text-[hsl(var(--bone))] text-sm font-medium">B</span>
          </div>
          <span className="font-sans text-[15px] font-semibold tracking-tight">
            Brickshare<span className="text-[hsl(var(--brand-ochre))]">.ai</span>
          </span>
        </button>
        <button
          onClick={() => navigate("/")}
          className="link-underline font-sans text-[13px] font-medium text-[hsl(var(--stone-600))] hover:text-[hsl(var(--ink))] transition-colors"
        >
          Back to site
        </button>
      </header>
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-10 pb-20">{children}</div>
    </div>
  );

  if (status === "loading") {
    return (
      <Shell>
        <div className="min-h-[60vh] flex items-center justify-center gap-3 text-[hsl(var(--stone-600))]">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="font-mono text-[12px] uppercase tracking-[0.1em]">Verifying invite</span>
        </div>
      </Shell>
    );
  }

  if (status === "invalid" || status === "used") {
    return (
      <Shell>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="max-w-md mx-auto mt-16 brand-card p-10 text-center"
        >
          <ShieldAlert className="w-12 h-12 text-[hsl(var(--brand-ochre))] mx-auto mb-5" />
          <p className="brand-eyebrow mb-2">Access</p>
          <h1 className="font-serif text-3xl tracking-[-0.02em] mb-4">
            {status === "invalid" ? "Invite Required" : "This invite has been used"}
          </h1>
          <p className="font-sans text-[15px] text-[hsl(var(--stone-600))] mb-7">
            {status === "invalid"
              ? "Access to Brickshare is currently limited. Request early access to join the waitlist."
              : "If you already have an account, sign in to continue."}
          </p>
          <div className="flex gap-3 justify-center">
            {status === "invalid" ? (
              <>
                <button
                  onClick={() => navigate("/waitlist")}
                  className="btn-forest font-sans text-[13px] font-medium h-10 px-5 rounded-[10px] inline-flex items-center gap-2"
                >
                  Request Access <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="btn-bone font-sans text-[13px] font-medium h-10 px-5 rounded-[10px] inline-flex items-center gap-2"
                >
                  Back to Home
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate("/auth")}
                className="btn-forest font-sans text-[13px] font-medium h-10 px-5 rounded-[10px] inline-flex items-center gap-2"
              >
                Sign In <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </motion.div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 pt-6 lg:pt-12">
        {/* Editorial column */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="lg:col-span-6 hidden lg:flex flex-col justify-center relative"
        >
          <div className="inline-flex w-fit items-center gap-2 px-3.5 py-1.5 rounded-full border border-[hsl(var(--stone-200))] bg-[hsl(var(--paper))]/80 backdrop-blur-sm mb-7">
            <CheckCircle2 className="w-3.5 h-3.5 text-[hsl(var(--brand-ochre))]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[hsl(var(--stone-600))]">
              Invite Approved
            </span>
          </div>
          <h1 className="font-serif text-[clamp(2.75rem,5.5vw,4.25rem)] leading-[1.0] tracking-[-0.03em] font-normal">
            <span className="block">You're in.</span>
            <span className="block">
              Build your <span className="italic font-medium text-[hsl(var(--brand-forest))]">workspace</span>.
            </span>
          </h1>
          <div className="brand-rule mt-6 mb-7" />
          <p className="font-sans text-[17px] leading-[1.55] text-[hsl(var(--stone-600))] max-w-md">
            Set a password to finish creating your account. Your invite is single-use and tied to your email.
          </p>
          <div className="mt-12 relative h-[180px]">
            <IsometricStack className="absolute -left-6 top-0 w-[280px] text-[hsl(var(--brand-forest-deep))] opacity-[0.10]" />
            <FloorPlanFragment className="absolute right-0 bottom-0 w-[220px] text-[hsl(var(--brand-forest-deep))] opacity-[0.08]" />
          </div>
        </motion.div>

        {/* Form column */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.1 }}
          className="lg:col-span-6 flex items-center justify-center"
        >
          <div className="w-full max-w-md brand-card p-8 lg:p-10">
            <div className="mb-7">
              <p className="brand-eyebrow mb-2">Create Account</p>
              <h2 className="font-serif text-3xl tracking-[-0.02em]">Almost there</h2>
            </div>

            <form onSubmit={handleCreate} className="space-y-5">
              <div className="space-y-1.5">
                <Label className="font-mono text-[11px] uppercase tracking-[0.1em] text-[hsl(var(--stone-600))]">Email</Label>
                <Input
                  type="email"
                  value={email}
                  disabled
                  className="h-11 bg-[hsl(var(--muted))] border-[hsl(var(--stone-200))] font-mono text-[13px]"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-mono text-[11px] uppercase tracking-[0.1em] text-[hsl(var(--stone-600))]">Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  className="h-11 bg-[hsl(var(--bone))] border-[hsl(var(--stone-200))] focus-visible:ring-[hsl(var(--brand-forest))] focus-visible:border-[hsl(var(--brand-forest))]"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-mono text-[11px] uppercase tracking-[0.1em] text-[hsl(var(--stone-600))]">Confirm Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  className="h-11 bg-[hsl(var(--bone))] border-[hsl(var(--stone-200))] focus-visible:ring-[hsl(var(--brand-forest))] focus-visible:border-[hsl(var(--brand-forest))]"
                />
              </div>

              {error && <p className="font-mono text-[12px] text-[hsl(var(--destructive))]">{error}</p>}

              <button
                type="submit"
                disabled={creating}
                className="btn-forest font-sans text-[14px] font-medium h-12 px-6 rounded-[10px] w-full inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>
                    <span>Create Account</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              {/* The other moment consent matters: an account is being created
                  here, and neither document was reachable from this screen. */}
              <p className="font-sans text-[12px] text-center leading-relaxed text-[hsl(var(--stone-600))]">
                By creating an account, you agree to our{" "}
                <a href="/terms" className="underline hover:text-[hsl(var(--ink))]">Terms of Service</a>
                {" "}and{" "}
                <a href="/privacy" className="underline hover:text-[hsl(var(--ink))]">Privacy Policy</a>.
              </p>
            </form>
          </div>
        </motion.div>
      </div>
    </Shell>
  );
}
